// card-sandbox: the host↔card postMessage protocol (ADR 2026-07-24-card-sandbox).
//
// A sandboxed (Tier-2) card runs in an isolated iframe and is a SEPARATE bundle
// from the host, so every byte crossing the boundary is this versioned contract.
// This module is deliberately origin-independent: it defines message shapes and
// validation only — nothing here touches the DOM, the iframe, or a real origin,
// so the opaque-origin (A1) vs distinct-origin (B) decision doesn't reach it.
//
// Direction convention: `host:*` flows host→card, `card:*` flows card→host.
//
// v2 (ADR 2026-07-24-card-sandbox-messaging) adds the host-mediated backend
// channel: `card:invoke` (request/response to the card's OWN helper) and
// `host:push` (server-originated, session-scoped). The card still has NO network
// — the host is the transport and the gate; CSP is unchanged.

export const PROTOCOL_VERSION = 2;

export const HOST = {
  INIT: 'host:init',                     // { protocol, kind, structured, theme, resync?, reduceMotion? }
  STORAGE_RESULT: 'host:storage:result', // { reqId, ok, value? }
  INVOKE_RESULT: 'host:invoke:result',   // { reqId, ok, value? } — reply to card:invoke (v2)
  PUSH: 'host:push',                     // { channel, message } — server-originated, session-scoped (v2)
  LIFECYCLE: 'host:lifecycle',           // { event: 'suspend'|'resume' } — chat-WS down/up (v2);
                                         // { event: 'hide'|'show' } — a live card out of / back in view
};

export const CARD = {
  READY: 'card:ready',               // { protocol }
  ACTION: 'card:action',             // { action, structured? } — fire-and-forget, surfaces in chat
  RESIZE: 'card:resize',             // { height }
  STORAGE: 'card:storage',           // { reqId, op: 'get'|'set', key, value? }
  INVOKE: 'card:invoke',             // { reqId, action, structured? } — request/response to own helper (v2)
  SWIPE: 'card:swipe',               // { dir: 'next'|'prev' } — a horizontal swipe the host pager may page on (card-pager-swipe)
  // "something is happening in here, and you may not be able to see it." A card that starts
  // moving on its own — a run beginning the moment its program is written — is invisible if
  // the chat has scrolled past it. The card ASKS; the host decides whether to scroll, so a
  // card can never yank the page around. Advisory, no payload, ignored when already visible.
  REVEAL: 'card:reveal',             // {} — bring me into view if I am not
  // A live card says it has finished (a run done, the trains gone): the host closes it and its
  // chat link shows "ended". Advisory, no payload; a card that is not live has nothing to end.
  END: 'card:end',                   // {}
};

const CARD_TYPES = new Set(Object.values(CARD));

// Is `msg` a structurally valid inbound card→host message? Validation is
// intentionally strict and total: an unknown type, a wrong-typed field, or a
// missing required field returns false and the caller DROPS it. A sandboxed card
// is untrusted — a malformed frame must never throw into the host or be acted on.
export function isValidCardMessage(msg) {
  if (!msg || typeof msg !== 'object' || !CARD_TYPES.has(msg.type)) return false;
  switch (msg.type) {
    case CARD.READY:
      // protocol is advisory at ready-time (the host decides compatibility);
      // accept the frame, let the host compare versions.
      return true;
    case CARD.ACTION:
      // `action` must be an object (an action_spec). It is NOT trusted here —
      // the host's action gate validates its contents before onAction.
      return !!msg.action && typeof msg.action === 'object';
    case CARD.RESIZE:
      return typeof msg.height === 'number' && isFinite(msg.height) && msg.height >= 0;
    case CARD.REVEAL:
    case CARD.END:
      // No payload to get wrong; the frame's existence IS the request.
      return true;
    case CARD.SWIPE:
      // Purely advisory UI hint (like resize): the host pager may page on it. Only the
      // two directions are valid; anything else is dropped.
      return msg.dir === 'next' || msg.dir === 'prev';
    case CARD.STORAGE:
      if (msg.op !== 'get' && msg.op !== 'set') return false;
      if (typeof msg.reqId !== 'string' || typeof msg.key !== 'string') return false;
      return true;
    case CARD.INVOKE:
      // request/response call to the card's OWN helper (v2). `action` is an
      // action_spec (object) — NOT trusted here; the host's action gate + identity
      // stamp validate it before dispatch. reqId correlates host:invoke:result.
      if (typeof msg.reqId !== 'string') return false;
      return !!msg.action && typeof msg.action === 'object';
    default:
      return false;
  }
}

// The oldest card protocol this host still speaks. A v1 (self-contained) card runs
// unchanged on the v2 host — the host simply never sends it the v2 push /
// invoke-result frames.
export const MIN_PROTOCOL_VERSION = 1;

// True when the card's advertised protocol is one this host can talk. The host is
// backward-compatible down to MIN_PROTOCOL_VERSION; a card NEWER than the host is
// refused (the host can't guarantee frames it doesn't know) → host renders the
// GenericCard fallback. Cards ship independently of the host, so skew is expected
// and handled, not assumed away.
export function protocolCompatible(cardProtocol) {
  return Number.isInteger(cardProtocol)
    && cardProtocol >= MIN_PROTOCOL_VERSION
    && cardProtocol <= PROTOCOL_VERSION;
}
